import React from 'react';
import { Shield, Users, Zap, Target, Clock, Award, CheckCircle2, TrendingUp } from 'lucide-react';

/**
 * Key Differentiators Component - Inspired by Smartsocs.com
 * - Grid of icon-based feature cards
 * - Clean, modern card design
 * - Hover animations
 */

const differentiators = [
    {
        icon: Users,
        title: "Domain Experts",
        description: "7,700+ specialized engineers with deep semiconductor expertise across all process nodes from 180nm to 3nm."
    },
    {
        icon: Target,
        title: "End-to-End Solutions",
        description: "Complete silicon lifecycle coverage from architecture and RTL through physical design, DFT, and post-silicon validation."
    },
    {
        icon: Zap,
        title: "Rapid Deployment",
        description: "Pre-screened talent pools enable us to staff complex programs in weeks, not months."
    },
    {
        icon: Shield,
        title: "Proven Methodologies",
        description: "CMMI Level 5 processes and ISO-certified quality systems ensure predictable delivery."
    },
    {
        icon: Clock,
        title: "Global Delivery",
        description: "7 delivery centers across 3 continents provide 24/7 engineering support and follow-the-sun coverage."
    },
    {
        icon: Award,
        title: "Industry Recognition",
        description: "Trusted by Fortune 500 semiconductor companies for over two decades of engineering excellence."
    }
];

const Differentiators: React.FC = () => {
    return (
        <section id="differentiators" className="py-24 bg-[#0a1628] relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#153860]/50 to-transparent" />

            <div className="relative z-10 max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#55C3BD]/10 border border-[#55C3BD]/30 text-[#55C3BD] text-xs font-semibold uppercase tracking-wider mb-6">
                        <CheckCircle2 size={14} /> Why LanceSoft VLSI
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                        Key <span className="text-[#55C3BD]">Differentiators</span>
                    </h2>
                    <p className="text-lg text-white/60 max-w-3xl mx-auto">
                        What sets us apart in the competitive semiconductor engineering landscape
                    </p>
                </div>

                {/* Differentiators Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {differentiators.map((item, idx) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={idx}
                                className="group bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#55C3BD]/50 hover:bg-white/5 transition-all duration-300"
                            >
                                {/* Icon */}
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#55C3BD] to-[#0079C2] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#55C3BD]/25 transition-all duration-300">
                                    <Icon size={28} className="text-white" />
                                </div>

                                {/* Content */}
                                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#55C3BD] transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-white/60 leading-relaxed">
                                    {item.description}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom Stats Bar */}
                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { value: "$550M+", label: "Annual Revenue" },
                        { value: "58", label: "Global Offices" },
                        { value: "200+", label: "Enterprise Clients" },
                        { value: "96.8%", label: "Retention Rate" }
                    ].map((stat, idx) => (
                        <div key={idx} className="text-center p-6 rounded-xl bg-white/5 border border-white/10">
                            <div className="text-3xl font-bold text-[#55C3BD] mb-2">{stat.value}</div>
                            <div className="text-sm text-white/60 uppercase tracking-wider">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Differentiators;
